import * as React from 'react';
import EmployeeView from 'about/components/EmployeeView';
import { graphql } from 'gatsby';
import useWindowSize from 'core/hooks/useWindowSize';
import { useState } from 'react';
import { BREAKPOINT_MD_MIN } from 'core/constants';
import { Swiper, SwiperSlide } from 'swiper/react';
import { ClinicTeamMemberWithImage } from 'lib/types';
import classnames from 'classnames';
import useHasIntersected from 'core/hooks/useHasIntersected';
import CallToAction from 'core/components/CallToAction';
import * as styles from './TeamMembers.module.scss';
import useSortedTeamMembers from './useSortedTeamMembers';
import type { MappedEmployee } from './useSortedTeamMembers';
import TeamMemberModal from './TeamMemberModal';
import 'swiper/css';

/* eslint-disable max-len */
export const query = graphql`
    fragment TeamMembersFragment on ClinicTeamMember {
        id
        name
        role
        leadershipTeam
        includePrefix
        locationIds
        pages
        bio {
            type
            value
            questions
        }
        image {
            file {
                mobile: childImageSharp {
                    gatsbyImageData(
                        width: 142
                        height: 166
                        sizes: "(max-width: 430px) calc((100vw - 98px) / 2.333), 168px"
                        transformOptions: { fit: COVER, cropFocus: ATTENTION }
                    )
                }
                desktop: childImageSharp {
                    gatsbyImageData(
                        width: 160
                        height: 168
                        sizes: "(max-width: 430px) calc((100vw - 98px) / 2.333), 168px"
                        transformOptions: { fit: COVER, cropFocus: ATTENTION }
                    )
                }
            }
        }
    }
    fragment AboutUsTeamMembersFragment on ClinicTeamMember {
        id
        clinicTeamMemberId
        name
        role
        leadershipTeam
        locationIds
        pages
        image {
            file {
                mobile: childImageSharp {
                    gatsbyImageData(
                        width: 133
                        height: 168
                        transformOptions: { fit: COVER, cropFocus: ATTENTION }
                    )
                }
                desktop: childImageSharp {
                    gatsbyImageData(
                        width: 133
                        height: 168
                        transformOptions: { fit: COVER, cropFocus: ATTENTION }
                    )
                }
            }
        }
    }
    fragment AboutUsLeadershipFragment on ClinicTeamMember {
        id
        clinicTeamMemberId
        name
        role
        includePrefix
        image {
            file {
                mobile: childImageSharp {
                    gatsbyImageData(
                        width: 133
                        height: 168
                        transformOptions: { fit: COVER, cropFocus: ATTENTION }
                    )
                }
                desktop: childImageSharp {
                    gatsbyImageData(
                        width: 133
                        height: 168
                        transformOptions: { fit: COVER, cropFocus: ATTENTION }
                    )
                }
            }
        }
    }
`;
/* eslint-enable max-len */

export type TeamMembersProps = {
    teamMembers: ReadonlyArray<ClinicTeamMemberWithImage>;
    expanderItemCount?: number;
    title: React.ReactNode;
    showAllMembers?: boolean;
    keepOriginalOrder?: boolean;
    alignCenter?: boolean;
    classes?: Partial<typeof styles>;
};

function TeamMembers({
    teamMembers,
    expanderItemCount = 8,
    title,
    showAllMembers,
    keepOriginalOrder,
    alignCenter,
    classes,
}: TeamMembersProps): React.ReactElement | null {
    const { width } = useWindowSize();

    // up until 1500px of width, we can only show 3 team members
    // per row, so if we limit it to 8, we will have an incomplete
    // row with only 2 items
    const maxTeamMembers = React.useMemo(() => {
        const itemsPerRow = width < 1500 ? 3 : 4;

        return expanderItemCount - (expanderItemCount % itemsPerRow);
    }, [expanderItemCount, width]);

    const [modalOpen, setModalOpen] = useState(false);
    const [modalContent, setModalContent] = useState<
        MappedEmployee | undefined
    >(undefined);
    const [showMore, setShowMore] = React.useState(showAllMembers || false);

    const toggleShowMore = React.useCallback(() => {
        setShowMore((prev) => !prev);
    }, []);

    const sortedTeamMembers = useSortedTeamMembers(teamMembers);

    const employees: ReadonlyArray<ClinicTeamMemberWithImage> =
        keepOriginalOrder ? teamMembers : sortedTeamMembers;

    const toggleModalOpen = React.useCallback(() => {
        setModalOpen((prev) => !prev);
    }, []);

    const onEmployeeClick = React.useCallback(
        (employee?: MappedEmployee) => {
            toggleModalOpen();
            setModalContent(employee);
        },
        [toggleModalOpen],
    );

    const rootRef = React.useRef(null);
    const hasIntersected = useHasIntersected(rootRef);

    const loading = hasIntersected ? 'eager' : 'lazy';

    if (!employees.length) {
        return null;
    }

    return (
        <>
            <section
                className={classnames(styles.root, classes?.root)}
                ref={rootRef}
            >
                <h2
                    className={classnames(styles.title, classes?.title, {
                        [styles.centered]: alignCenter,
                    })}
                >
                    {title}
                </h2>
                {width > BREAKPOINT_MD_MIN ? (
                    <>
                        <ul
                            className={classnames(styles.grid, {
                                [styles.centered]: alignCenter,
                                [styles.showAll]: showMore,
                            })}
                        >
                            {employees.map((employee) => (
                                <li
                                    key={employee.id}
                                    className={styles.employeeEntry}
                                >
                                    <EmployeeView
                                        onClick={onEmployeeClick}
                                        employee={employee}
                                        openModalOnClick={!!employee.bio}
                                        showDescriptionOnHover
                                        showChevron={!!employee.bio}
                                        classes={{
                                            image: classnames(
                                                styles.image,
                                                classes?.image,
                                            ),
                                            imageWrapper: styles.imageWrapper,
                                            employee: styles.employee,
                                            name: styles.name,
                                        }}
                                        loading={loading}
                                    />
                                </li>
                            ))}
                        </ul>
                        {!showAllMembers &&
                            employees.length > maxTeamMembers && (
                                <CallToAction
                                    variant="outlined"
                                    onClick={toggleShowMore}
                                    size="small"
                                >
                                    {showMore ? 'Show Less' : 'Show All'}
                                </CallToAction>
                            )}
                    </>
                ) : (
                    <div className={styles.swiperWrapper}>
                        <Swiper
                            slidesPerView="auto"
                            loop={employees.length > 3}
                            preloadImages
                            centeredSlides={employees.length > 3}
                            className={styles.swiper}
                            spaceBetween={40}
                        >
                            {employees.map((employee) => (
                                <SwiperSlide
                                    key={employee.id}
                                    className={styles.swiperSlide}
                                >
                                    <div className={styles.employeeEntry}>
                                        <EmployeeView
                                            onClick={onEmployeeClick}
                                            key={employee.id}
                                            employee={employee}
                                            openModalOnClick={!!employee.bio}
                                            showDescriptionOnHover
                                            showChevron={!!employee.bio}
                                            classes={{
                                                image: styles.image,
                                                imageWrapper:
                                                    styles.imageWrapper,
                                                employee: styles.employee,
                                                name: styles.name,
                                            }}
                                            loading={loading}
                                        />
                                    </div>
                                </SwiperSlide>
                            ))}
                        </Swiper>
                    </div>
                )}
                <hr className={styles.hr} />
            </section>
            <TeamMemberModal
                modalOpen={modalOpen}
                onClose={toggleModalOpen}
                employee={modalContent}
            />
        </>
    );
}

export default React.memo(TeamMembers);
